import React, { useState } from 'react';
import '../../pages/AdminCreateTablePage/styles.css';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const SchemaCreateTablePanel = ({ entityId, onClose, onSuccess }) => {
  const [tableName, setTableName] = useState('');
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const dataTypes = [
    { value: 'text', label: 'Text' },
    { value: 'integer', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'time', label: 'Time' },
    { value: 'boolean', label: 'Yes/No' },
    { value: 'email', label: 'Email' }
  ];

  const addColumn = () => {
    setColumns([...columns, { name: '', type: 'text', required: false }]);
  };

  const removeColumn = (index) => {
    const newColumns = columns.filter((_, i) => i !== index);
    setColumns(newColumns);
  };

  const updateColumn = (index, field, value) => {
    const newColumns = [...columns];
    newColumns[index][field] = value;
    setColumns(newColumns);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!tableName.trim()) {
      setError('Table name is required');
      return;
    }

    if (columns.length === 0) {
      setError('At least one column is required');
      return;
    }

    // Validate columns
    for (let i = 0; i < columns.length; i++) {
      const column = columns[i];
      if (!column.name.trim()) {
        setError(`Column ${i + 1} name is required`);
        return;
      }
    }

    // Check for duplicate column names
    const columnNames = columns.map(col => col.name.toLowerCase());
    const uniqueNames = new Set(columnNames);
    if (uniqueNames.size !== columnNames.length) {
      setError('Column names must be unique');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/schemas`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          table_name: tableName.trim(),
          columns: columns.map(col => ({
            name: col.name.trim(),
            type: col.type,
            required: col.required
          })),
          entity_id: entityId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to create table');
      }

      setSuccess('Table created successfully!');
      setTimeout(() => {
        onSuccess?.();
        onClose?.();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="schema-create-table-panel">
      <div className="create-table-content">
        <form onSubmit={handleSubmit} className="create-table-form">
          <div className="form-section">
            <h2>Table Information</h2>
            <div className="form-group">
              <label htmlFor="tableName">Table Name *</label>
              <input
                id="tableName"
                type="text"
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="Enter table name (e.g., Student Records)"
                className="form-input"
                required
              />
            </div>
          </div>

          <div className="form-section">
            <div className="form-section-header">
              <h2>Table Columns</h2>
              <button
                type="button"
                onClick={addColumn}
                className="add-column-btn"
              >
                + Add Column
              </button>
            </div>

            {columns.length === 0 ? (
              <div className="no-columns-message">
                <p>No columns added yet. Click "Add Column" to get started.</p>
              </div>
            ) : (
              <div className="columns-list">
                {columns.map((column, index) => (
                  <div key={index} className="column-item">
                    <div className="column-fields">
                      <div className="form-group">
                        <label>Column Name *</label>
                        <input
                          type="text"
                          value={column.name}
                          onChange={(e) => updateColumn(index, 'name', e.target.value)}
                          placeholder="Enter column name"
                          className="form-input"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label>Data Type *</label>
                        <select
                          value={column.type}
                          onChange={(e) => updateColumn(index, 'type', e.target.value)}
                          className="form-select"
                        >
                          {dataTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group checkbox-group">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={column.required}
                            onChange={(e) => updateColumn(index, 'required', e.target.checked)}
                            className="checkbox-input"
                          />
                          <span className="checkbox-text">Required</span>
                        </label>
                      </div>

                      <button
                        type="button"
                        onClick={() => removeColumn(index)}
                        className="remove-column-btn"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="cancel-btn"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || columns.length === 0}
              className="submit-btn"
            >
              {loading ? 'Creating...' : 'Create Table'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default SchemaCreateTablePanel;

